import { useEffect, useRef, useState } from "react";

const SEEN_KEY = "mt_exit_popup_seen_v1";
const FALLBACK_MS = 90_000;

const readSeen = () => {
  try {
    return Boolean(localStorage.getItem(SEEN_KEY));
  } catch (err) {
    console.warn("[exit-intent] localStorage read failed:", err);
    return false;
  }
};

const writeSeen = () => {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch (err) {
    console.warn("[exit-intent] localStorage write failed:", err);
  }
};

/**
 * useExitIntent
 *
 * Returns `{ open, dismiss }`. Sets `open` to `true` exactly once per visitor
 * when:
 *   - the cursor leaves the viewport from the top (desktop intent), OR
 *   - 90s pass without prior trigger (fallback for mobile + slow visits).
 *
 * Suppressed on `/admin/*` routes and remembered across reloads via
 * `localStorage` (non-sensitive, just a boolean seen-flag).
 */
export const useExitIntent = () => {
  const [open, setOpen] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.location.pathname.startsWith("/admin")) return undefined;
    if (readSeen()) return undefined;

    const trigger = () => {
      if (triggered.current) return;
      triggered.current = true;
      setOpen(true);
      writeSeen();
    };

    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };

    const timer = setTimeout(trigger, FALLBACK_MS);
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => setOpen(false);

  return { open, dismiss };
};

export default useExitIntent;
